import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Copy, ExternalLink, QrCode, CreditCard, Link } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface PaymentMethod {
  id: string;
  method_type: string;
  method_name: string;
  is_active: boolean;
  configuration: any;
  qr_code_url?: string;
  payment_link?: string;
  bank_details?: any;
}

interface StudentPaymentDialogProps {
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  setOpen?: (open: boolean) => void;
  paymentAmount?: number;
  studentName?: string;
}

const StudentPaymentDialog: React.FC<StudentPaymentDialogProps> = ({
  open,
  isOpen,
  onOpenChange,
  setOpen,
  paymentAmount = 650,
  studentName = ''
}) => {
  useUiLanguage();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('payments');

  // Handle both prop naming conventions
  const dialogOpen = open ?? isOpen ?? false;
  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange?.(newOpen);
    setOpen?.(newOpen);
  };

  useEffect(() => {
    if (dialogOpen) {
      fetchPaymentMethods();
    }
  }, [dialogOpen]);

  const fetchPaymentMethods = async () => {
    if (!profile?.dojo_id) return;

    const { data, error } = await supabase
      .from('payment_methods')
      .select('*')
      .eq('dojo_id', profile.dojo_id)
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching payment methods:', error);
      return;
    }

    setPaymentMethods(data || []);
    if (data && data.length > 0) {
      setSelectedMethod(data[0]);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: uiText("Copied!"),
        description: uiText("{{value0}} copied to clipboard", { value0: label })
      });
    });
  };

  const getCurrencySymbol = () => {
    const currency = selectedMethod?.configuration?.currency || 'CHF';
    switch (currency) {
      case 'CHF': return 'CHF';
      case 'EUR': return '€';
      case 'USD': return '$';
      default: return currency;
    }
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'qr_code': return <QrCode className="h-5 w-5" />;
      case 'payment_link': return <Link className="h-5 w-5" />;
      default: return <CreditCard className="h-5 w-5" />;
    }
  };

  if (paymentMethods.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{uiText("Payment Information")}</DialogTitle>
            <DialogDescription>
              {uiText("No payment methods are currently available. Please contact your dojo administrator.")}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
            {uiText("Complete Your Subscription Payment")}</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-300">
            {uiText("Choose a payment method to complete your subscription")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Payment Amount */}
          <div className="bg-blue-50 dark:bg-blue-900/30 p-3 sm:p-4 rounded-lg border dark:border-blue-800">
            <div className="text-center">
              <h3 className="text-base sm:text-lg font-semibold text-blue-900 dark:text-blue-100">
                {uiText("Payment Amount")}</h3>
              <div className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-300 mt-2">
                {uiText("CHF")}{' '}{(paymentAmount / 100).toFixed(2)}
              </div>
              {studentName && (
                <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-200 mt-1">
                  {uiText("For:")}{' '}{studentName}
                </p>
              )}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="space-y-4">
            <h4 className="font-medium">{uiText("Choose Payment Method")}</h4>

            {paymentMethods.length > 0 ? (
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <Card
                    key={method.id}
                    className={`cursor-pointer transition-colors ${
                      selectedMethod?.id === method.id ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedMethod(method)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full border-2 border-blue-500 flex items-center justify-center">
                            {selectedMethod?.id === method.id && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{method.method_name}</div>
                            <div className="text-sm text-gray-500 capitalize">
                              {method.method_type.replace('_', ' ')}
                            </div>
                          </div>
                        </div>
                        <CreditCard className="h-5 w-5 text-gray-400" />
                      </div>

                      {/* Payment Method Details */}
                      {selectedMethod?.id === method.id && (
                        <div className="mt-4 space-y-3">
                          {method.method_type === 'bank_transfer' && method.bank_details && (
                            <div className="bg-white p-3 rounded border space-y-2">
                              {method.bank_details.iban && (
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">{uiText("IBAN:")}</span>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs">{method.bank_details.iban}</code>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => copyToClipboard(method.bank_details.iban, 'IBAN')}
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {method.bank_details.recipient && (
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">{uiText("Recipient:")}</span>
                                  <span className="text-sm">{method.bank_details.recipient}</span>
                                </div>
                              )}
                              {method.bank_details.reference && (
                                <div className="text-xs text-gray-600 p-2 bg-gray-50 rounded">
                                  <strong>{uiText("Reference:")}</strong> {method.bank_details.reference}
                                </div>
                              )}
                            </div>
                          )}

                          {method.method_type === 'payment_link' && method.payment_link && (
                            <Button
                              onClick={() => window.open(method.payment_link, '_blank')}
                              className="w-full"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              {uiText("Open Payment Link")}</Button>
                          )}

                          {method.method_type === 'stripe' && (
                            <Button
                              onClick={() => window.open('https://buy.stripe.com/fZu5kD06o4U37Ha8WTdAk00', '_blank', 'noopener,noreferrer')}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              {uiText("Pay with Stripe")}</Button>
                          )}

                          {method.qr_code_url && (
                            <div className="text-center">
                              <p className="text-sm font-medium mb-2">{uiText("Scan to Pay")}</p>
                              <img
                                src={method.qr_code_url}
                                alt={uiText("Payment QR Code")}
                                className="mx-auto max-w-32 h-auto border rounded"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CreditCard className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500">
                  {uiText("No payment methods available. Contact your instructor for payment details.")}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentPaymentDialog;